import {
  type Project,
  type Values,
  type OutputProfile,
  type Field,
} from "@proof/core";
import { type AssetInfo } from "@proof/ui";
export interface JobView {
  id: string;
  duplicate?: boolean;
  replacesJobId?: string;
  profileId?: string;
  state:
    "queued" | "running" | "complete" | "failed" | "cancelled" | "quarantined";
  projectId: string;
  projectName: string;
  profileName: string;
  attempts: number;
  stateVersion: number;
  attemptRecords: {
    id: string;
    generation: number;
    state: string;
    probation: boolean;
    reason: string;
    leaseUntil?: string;
  }[];
  createdAt: string;
  events: { at: string; message: string }[];
  error?: string;
  retryable?: boolean;
  artifact?: {
    filename: string;
    mime: string;
    bytes: number;
    sha256: string;
    pageCount: number;
    sheetLayout?: {
      sheets: number;
      copies: number;
      capacity: number;
      rotation: number;
      material?: string;
      physicalSheets?: number;
      materialCost?: {amount:number;currency:string};
    };
    colorSpace: string;
    pressReady: boolean;
    diagnostics: { message: string }[];
    viewAllowed: boolean;
    downloadAllowed: boolean;
  };
}
export async function api<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const r = await fetch(`/api${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...options.headers },
  });
  if (!r.ok) {
    const message = await r.json().catch(() => ({ error: r.statusText }));
    throw new Error(message.error || r.statusText);
  }
  return r.json();
}
export const generate = (
  project: Project,
  values: Values,
  profile: OutputProfile,
  partId?: string,
  contextOptions: { demoProfile?: string; contextHash?: string } = {},
) =>
  api<JobView>("/jobs", {
    method: "POST",
    body: JSON.stringify({
      project,
      values,
      profile,
      partId,
      ...contextOptions,
    }),
  });
export async function upload(
  file: File,
  _field?: Field,
  signal?: AbortSignal,
): Promise<AssetInfo> {
  const r = await fetch("/api/assets", {
    method: "POST",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      "X-Filename": encodeURIComponent(file.name),
    },
    body: file,
    signal,
  });
  if (!r.ok) throw new Error((await r.json()).error);
  return r.json();
}
export function downloadJson(value: unknown, name: string) {
  downloadBlob(
    new Blob([JSON.stringify(value, null, 2)], { type: "application/json" }),
    name,
  );
}
export function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob),
    a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface BulkView {
  id: string;
  state: string;
  stateVersion: number;
  projectId: string;
  projectName: string;
  source: import("@proof/core").BulkSource;
  mapping: import("@proof/core").BulkRequest["mapping"];
  progress: {
    total: number;
    pending: number;
    running: number;
    succeeded: number;
    failed: number;
    cancelled: number;
    processed: number;
    percent: number;
  };
  rows: {
    key: string;
    submissionId: string;
    sourceRow: number;
    state: string;
    diagnostic?: { code: string; message: string; retryable: boolean };
    job?: JobView;
  }[];
}
